import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

// Unsaved-changes guard (build plan §5.5).
//
// While `dirty`, every in-app navigation (links, tabs, the nav drawer, Back)
// is held with history.block and opens the Leave sheet; a tab close or
// reload gets the browser's own beforeunload prompt.
//
// Returns
//   pending     the held navigation ({ location, action }) or action
//               ({ run }), or null — the Leave sheet is open while set
//   guard(run)  run now when clean, else hold it behind the Leave sheet
//               (Archive / Duplicate use this)
//   saveAndLeave()  await onSave(); on success continue
//   discardAndLeave()  onDiscard(); continue
//   keepEditing()   drop the held navigation
//   release()   lift the block now (before navigating away on purpose)
const useUnsavedGuard = ({ dirty, onSave, onDiscard }) => {
  const history = useHistory();
  const [pending, setPending] = useState(null);
  const unblockRef = useRef(null);

  const release = useCallback(() => {
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    unblockRef.current = history.block((location, action) => {
      setPending({ location, action });
      return false;
    });
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      release();
    };
  }, [dirty, history, release]);

  const proceed = useCallback(
    (held) => {
      release();
      if (!held) return;
      if (held.run) {
        held.run();
        return;
      }
      if (held.action === 'REPLACE') history.replace(held.location);
      else history.push(held.location);
    },
    [history, release]
  );

  const guard = useCallback(
    (run) => {
      if (dirty) setPending({ run });
      else run();
    },
    [dirty]
  );

  const saveAndLeave = useCallback(async () => {
    const held = pending;
    setPending(null);
    const ok = await onSave();
    if (ok) proceed(held);
  }, [pending, onSave, proceed]);

  const discardAndLeave = useCallback(() => {
    const held = pending;
    setPending(null);
    onDiscard();
    proceed(held);
  }, [pending, onDiscard, proceed]);

  const keepEditing = useCallback(() => setPending(null), []);

  return {
    pending,
    guard,
    saveAndLeave,
    discardAndLeave,
    keepEditing,
    release,
  };
};

export default useUnsavedGuard;
